/**
 * Admin UI Design Tokens
 * 
 * ⚠️ IMPORTANT: CSS variables in clc.css and design-tokens.css are the source of truth.
 * These JS tokens are provided for backward compatibility with inline styles.
 * Prefer CSS classes and CSS variables over these JS tokens.
 * 
 * Spacing scale: 8px base unit (matches CSS --spacing-* variables)
 */

export const spacing = {
  // Aligned with CSS --spacing-* variables (8px base unit)
  xs: '0.25rem',    // 4px (legacy, use --spacing-1/2 if possible)
  sm: '0.5rem',     // 8px (matches --spacing-1)
  md: '1rem',       // 16px (matches --spacing-2) - CHANGED from 0.75rem
  lg: '1.5rem',     // 24px (matches --spacing-3) - CHANGED from 1rem
  xl: '2rem',       // 32px (matches --spacing-4) - CHANGED from 1.5rem
  '2xl': '3rem',    // 48px (matches --spacing-6) - CHANGED from 2rem
  '3xl': '4rem',    // 64px (matches --spacing-8) - CHANGED from 3rem
  // Additional spacing values matching CSS
  '4': '2rem',      // 32px (matches --spacing-4)
  '6': '3rem',      // 48px (matches --spacing-6)
  '8': '4rem',      // 64px (matches --spacing-8)
};

/**
 * ⚠️ DEPRECATED: Typography tokens should use CSS classes or CSS variables.
 * CSS source of truth: design-tokens.css and admin-ui.css
 * 
 * These are kept for backward compatibility only.
 * h1: 2rem (32px) - matches CSS
 * h2: 1.25rem (20px) - matches CSS (was 1.5rem, FIXED)
 * h3: 1rem (16px) - matches CSS
 */
export const typography = {
  h1: {
    fontSize: '2rem',        // 32px - matches CSS
    fontWeight: '700',
    lineHeight: '1.25',      // Updated to match CSS
    color: 'var(--color-text-primary)', // Use CSS variable
    margin: 0,
  },
  h2: {
    fontSize: '1.25rem',     // 20px - FIXED: was 1.5rem, now matches CSS
    fontWeight: '600',
    lineHeight: '1.3',       // Updated to match CSS
    color: 'var(--color-text-primary)', // Use CSS variable
    margin: 0,
  },
  h3: {
    fontSize: '1rem',        // 16px - matches CSS
    fontWeight: '600',
    lineHeight: '1.5',       // Updated to match CSS
    color: 'var(--color-text-primary)', // Use CSS variable
    margin: 0,
  },
  body: {
    fontSize: '0.875rem',    // 14px - matches CSS
    fontWeight: '400',
    lineHeight: '1.5',       // Matches CSS
    color: 'var(--color-text-primary)', // Use CSS variable
  },
  bodySmall: {
    fontSize: '0.75rem',     // 12px - matches CSS
    fontWeight: '400',
    lineHeight: '1.5',       // Matches CSS
    color: 'var(--color-text-tertiary)', // Use CSS variable
  },
  subtle: {
    fontSize: '0.875rem',    // 14px
    fontWeight: '400',
    lineHeight: '1.5',
    color: 'var(--color-text-tertiary)', // Use CSS variable
  },
  label: {
    fontSize: '0.75rem',     // 12px - matches CSS --font-size-xs
    fontWeight: '500',
    lineHeight: '1.4',
    color: 'var(--color-text-secondary)', // Use CSS variable
  },
};

/**
 * ⚠️ DEPRECATED: Color tokens should use CSS variables directly.
 * CSS source of truth: clc.css
 * 
 * These are kept for backward compatibility only.
 * Components should use CSS variables: var(--color-*)
 */
export const colors = {
  // Semantic colors - match CSS --color-semantic-* variables
  success: {
    bg: 'var(--color-semantic-success-bg)',
    border: 'var(--color-semantic-success-base)',
    text: 'var(--color-semantic-success-text)',
    base: 'var(--color-semantic-success-base)',
  },
  warn: {
    bg: 'var(--color-semantic-warning-bg)',
    border: 'var(--color-semantic-warning-base)',
    text: 'var(--color-semantic-warning-text)',
    base: 'var(--color-semantic-warning-base)',
  },
  error: {
    bg: 'var(--color-semantic-error-bg)',
    border: 'var(--color-semantic-error-base)',
    text: 'var(--color-semantic-error-text)',
    base: 'var(--color-semantic-error-base)',
  },
  info: {
    bg: 'var(--color-semantic-info-bg)',
    border: 'var(--color-semantic-info-base)',
    text: 'var(--color-semantic-info-text)',
    base: 'var(--color-semantic-info-base)',
  },
  neutral: {
    bg: 'var(--color-bg-tertiary)',
    border: 'var(--color-border-medium)',
    text: 'var(--color-text-secondary)',
    base: 'var(--color-text-tertiary)',
  },
  // Base colors - use CSS variables
  background: {
    primary: 'var(--color-bg-primary)',
    secondary: 'var(--color-bg-secondary)',
    tertiary: 'var(--color-bg-tertiary)',
  },
  border: {
    light: 'var(--color-border-light)',
    medium: 'var(--color-border-medium)',
    dark: 'var(--color-border-strong)',
  },
  text: {
    primary: 'var(--color-text-primary)',
    secondary: 'var(--color-text-secondary)',
    tertiary: 'var(--color-text-tertiary)',
    inverse: 'var(--color-text-inverse)',
  },
};

export const borderRadius = {
  sm: '0.25rem',   // 4px
  md: '0.375rem',  // 6px
  lg: '0.5rem',    // 8px
  full: '9999px',
};

export const shadows = {
  sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  md: '0 1px 3px rgba(0, 0, 0, 0.1)',
  lg: '0 4px 6px rgba(0, 0, 0, 0.1)',
};

export const transitions = {
  fast: '150ms ease',
  normal: '200ms ease',
  slow: '300ms ease',
};

// Button variants
export const buttonVariants = {
  primary: {
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    '&:hover': {
      backgroundColor: '#2563eb',
    },
    '&:disabled': {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
      opacity: 0.6,
    },
  },
  secondary: {
    backgroundColor: '#ffffff',
    color: '#374151',
    border: '1px solid #d1d5db',
    '&:hover': {
      backgroundColor: '#f9fafb',
      borderColor: '#9ca3af',
    },
    '&:disabled': {
      backgroundColor: '#f3f4f6',
      color: '#9ca3af',
      cursor: 'not-allowed',
      opacity: 0.6,
    },
  },
  destructive: {
    backgroundColor: '#ef4444',
    color: '#ffffff',
    border: 'none',
    '&:hover': {
      backgroundColor: '#dc2626',
    },
    '&:disabled': {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
      opacity: 0.6,
    },
  },
  ghost: {
    backgroundColor: 'transparent',
    color: '#374151',
    border: 'none',
    '&:hover': {
      backgroundColor: '#f3f4f6',
    },
    '&:disabled': {
      color: '#9ca3af',
      cursor: 'not-allowed',
      opacity: 0.6,
    },
  },
};

// Badge variants
export const badgeVariants = {
  success: {
    backgroundColor: colors.success.bg,
    color: colors.success.text,
    border: `1px solid ${colors.success.border}`,
  },
  warn: {
    backgroundColor: colors.warn.bg,
    color: colors.warn.text,
    border: `1px solid ${colors.warn.border}`,
  },
  error: {
    backgroundColor: colors.error.bg,
    color: colors.error.text,
    border: `1px solid ${colors.error.border}`,
  },
  info: {
    backgroundColor: colors.info.bg,
    color: colors.info.text,
    border: `1px solid ${colors.info.border}`,
  },
  neutral: {
    backgroundColor: colors.neutral.bg,
    color: colors.neutral.text,
    border: `1px solid ${colors.neutral.border}`,
  },
};

